<template>
  <v-card class="pa-3" v-if="superUser || permission" :value="$route.path">
    <v-row class="d-flex justify-center">
      <v-col cols="12" md="12" sm="12">
        <v-card elevation="0" class="mt-1 pa-2">
          <v-divider></v-divider>
          <v-card-title class="subtitle-2 font-weight-medium">
            {{ translations.mappingDescription[lang] }}
          </v-card-title>
          <v-divider></v-divider>
          <v-row justify="center" class="mt-5 mb-3">
            <v-expansion-panels popout focusable @change="onPanelChange">
              <v-expansion-panel
                v-for="(item, i) in eventResponses"
                :key="item.event_name"
                class="mb-2"
              >
                <v-expansion-panel-header>
                  <template v-slot:actions>
                    <v-icon color="primary"> $expand </v-icon>
                  </template>
                  <span class="text-capitalize">{{
                    item.event_name
                  }}</span></v-expansion-panel-header
                >
                <v-expansion-panel-content>
                  <v-row class="mb-1">
                    <v-col
                      cols="12"
                      md="6"
                      class="d-flex justify-start pb-0 response-structure-toggle-wrap"
                    >
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            :class="[
                              'mapping-layout-toggle response-structure-toggle-btn',
                              showResponseStructure && 'response-structure-toggle-btn--open',
                            ]"
                            small
                            outlined
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click="toggleResponseStructure"
                          >
                            <v-icon size="16">
                              {{
                                showResponseStructure
                                  ? icons.mdiChevronDoubleLeft
                                  : icons.mdiChevronDoubleRight
                              }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>
                          {{
                            showResponseStructure
                              ? 'Hide response structure'
                              : 'Show response structure'
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                  <v-row class="mt-3 response-panels-row">
                    <v-col
                      cols="12"
                      md="12"
                      sm="12"
                      class="pb-2 response-structure-col"
                      :style="responseStructureColStyle"
                    >
                      <v-col
                        cols="12"
                        md="12"
                        :class="[
                          'pa-3 response-structure-frame',
                          showResponseStructure && 'response-structure-frame--open',
                        ]"
                        style="overflow-x: auto; border-radius: 8px"
                      >
                        <v-col cols="12" md="12">
                          <span class="text-h4 mb-3 pb-3">{{
                            translations.responseStructure[lang]
                          }}</span></v-col
                        >
                        <span class="mt-3 pt-3">
                          <pre>{{ JSON.stringify(item.response, null, 2) }}</pre>
                        </span>
                      </v-col>
                    </v-col>
                    <v-divider
                      vertical
                      class="response-structure-divider"
                      :style="responseStructureDividerStyle"
                    ></v-divider>
                    <v-col
                      cols="12"
                      md="12"
                      sm="12"
                      class="d-flex response-mapping-col"
                      :style="responseMappingColStyle"
                    >
                      <v-col
                        cols="12"
                        md="12"
                        class="pa-3"
                        style="overflow-x: auto; border-radius: 8px"
                        :class="[
                          (error.mapper[item.event_name] && 'red-area') || '',
                          (success.mapper[item.event_name] && 'green-area') || '',
                        ]"
                      >
                        <v-col cols="12" md="12">
                          <span class="text-h4 mb-3 pb-3">{{
                            translations.responseMapping[lang]
                          }}</span></v-col
                        >
                        <v-jsoneditor
                          v-if="responseMaps[i]"
                          :ref="`responseEditor-${item.event_id}`"
                          :options="options"
                          height="940px"
                          :plus="false"
                          v-model="responseMaps[i].response"
                          @error="showJsonError(item)"
                        ></v-jsoneditor>
                        <v-col cols="12" md="12" class="d-flex justify-center">
                          <span
                            color="error"
                            v-if="error.mapper[item.event_name]"
                            class="text-body-2 ma-3 pa-3 text-center red--text"
                            ><b>Error:</b> {{ error.mapper[item.event_name] }}</span
                          ></v-col
                        >
                      </v-col>
                    </v-col>
                  </v-row>
                  <v-col cols="12" md="12" class="pa-0">
                    <v-toolbar flat style="background: transparent">
                      <v-spacer></v-spacer>
                      <v-btn
                        type="submit"
                        :color="'secondary'"
                        class="text-capitalize px-5"
                        style="font-size: 0.9rem"
                        depressed
                        :loading="loading"
                        :disabled="
                          !getPermission(
                            item.store_event_response_mapper_id ? 'update' : 'create'
                          ) || !valid.mapper[item.event_name]
                        "
                        @click="saveMapper(item)"
                        >{{ translations.save[lang] }}
                      </v-btn>
                    </v-toolbar>
                  </v-col>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import {
  mdiCheckCircleOutline,
  mdiMinusCircleOutline,
  mdiClose,
  mdiCheckboxMarkedOutline,
  mdiTableEye,
  mdiChevronDoubleLeft,
  mdiChevronDoubleRight,
} from '@mdi/js';
import { mapState, mapMutations, mapActions } from 'vuex';

import translations from '@/utils/translations/general/settings/developers';
import validators from '@/utils/validators/general/settings/developers/webhookResponses';

export default {
  name: 'WebhookResponses',
  mixins: [translations, validators],

  data() {
    return {
      icons: {
        mdiCheckCircleOutline,
        mdiMinusCircleOutline,
        mdiClose,
        mdiCheckboxMarkedOutline,
        mdiTableEye,
        mdiChevronDoubleLeft,
        mdiChevronDoubleRight,
      },
      detailsDialog: false,
      showResponseStructure: true,
      autocompleteBoundEditors: {},
      options: {
        mode: 'code',
        mainMenuBar: false,
        statusBar: false,
        showErrorTable: false,
      },
    };
  },

  computed: {
    ...mapState(['loading', 'errorMessage', 'superUser', 'permissions', 'storePermissions']),

    ...mapState('general/settings/developers/webhookResponses', ['responses', 'mappers', 'mapper']),

    lang() {
      return this.$route.params.lang;
    },

    permission() {
      return (
        this.superUser ||
        (this.$store.state.permissions?.developers
          ? this.$store.state.permissions.developers.webhooks_responses?.read
          : null)
      );
    },

    eventResponses() {
      return this.responses.map((e) => {
        let responseMapper = this.getEventResponseMapperByEventId(e.event_id) || {};
        return {
          ...e,
          mapper: responseMapper.response || {},

          store_event_response_mapper_id: responseMapper.store_event_response_mapper_id || null,
        };
      });
    },

    mappersData() {
      let mapData = this.mappers || [];
      if (!mapData?.length) {
        this.responses.forEach((e) => {
          mapData.push({ response: {}, event_id: e.event_id });
        });
      } else if (mapData.length < this.responses?.length) {
        this.responses.forEach((e) => {
          if (!mapData.find((x) => x.event_id === e.event_id)) {
            mapData.push({
              response: {},
              event_id: e.event_id,
              store_event_response_mapper_id: null,
              store_id: mapData[0].store_id,
            });
          }
        });
      }
      return mapData;
    },

    responseMaps() {
      let maps = [];
      this.responses.forEach((e) => {
        let map = this.mappersData.find((x) => x.event_id == e.event_id) || {
          response: {},
          event_id: e.event_id,
          store_event_response_mapper_id: null,
        };
        if (Array.isArray(map?.response) && map.response.length === 0) {
          map.response = {};
        }
        maps = [...maps, map];
      });
      return maps;
    },

    responseVariablesByEvent() {
      const data = {};
      this.responses.forEach((responseItem) => {
        data[responseItem.event_id] = this.getNestedVariableEntries(responseItem.response);
      });
      return data;
    },

    responseMappingColStyle() {
      return {
        flex: this.showResponseStructure ? '0 0 50%' : '0 0 100%',
        maxWidth: this.showResponseStructure ? '50%' : '100%',
      };
    },

    responseStructureColStyle() {
      return {
        flex: this.showResponseStructure ? '0 0 50%' : '0 0 0%',
        maxWidth: this.showResponseStructure ? '50%' : '0%',
        transform: this.showResponseStructure ? 'scaleX(1)' : 'scaleX(0.02)',
        opacity: this.showResponseStructure ? '1' : '0.9',
        pointerEvents: this.showResponseStructure ? 'auto' : 'none',
      };
    },

    responseStructureDividerStyle() {
      return {
        opacity: this.showResponseStructure ? '1' : '0',
        maxWidth: this.showResponseStructure ? '1px' : '0px',
        minWidth: this.showResponseStructure ? '1px' : '0px',
        transition: 'all 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
      };
    },

    headers() {
      return [
        {
          text: this.translations.total[this.lang],
          value: 'total',
          align: 'center',
        },
        {
          text: this.translations.paymentStatus[this.lang],
          value: 'pending',
          align: 'center',
        },
        {
          text: this.translations.period[this.lang],
          value: 'periodVal',
          align: 'center',
        },
        {
          text: this.translations.payment_date[this.lang],
          value: 'updated_at',
          align: 'center',
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
          align: 'center',
        },
      ];
    },
  },

  methods: {
    ...mapActions('general/settings/developers/webhookResponses', [
      'getAllResponses',
      'getEventMappers',
      'getEventMapper',
      'createEventMapper',
      'updateEventMapper',
    ]),
    ...mapMutations(['setDeleteDialog', 'setNotification', 'setLoading', 'setErrorMessage']),

    getEventDescription(id) {
      return ((this.events || []).find((x) => x.id == id) || {}).description?.[this.lang] || '';
    },

    getEventResponseMapperByEventId(id) {
      return (this.mappersData || []).find((x) => x.event_id == id) || {};
    },

    toggleResponseStructure() {
      this.showResponseStructure = !this.showResponseStructure;
    },

    onPanelChange(index) {
      const item = this.eventResponses[index];
      if (!item) {
        return;
      }
      this.$nextTick(() => {
        this.setupAutocomplete(item);
      });
    },

    getNestedVariableEntries(value, base = '', entries = []) {
      if (Array.isArray(value)) {
        if (!value.length) {
          return entries;
        }
        const arrayBase = base ? `${base}.0` : '0';
        this.getNestedVariableEntries(value[0], arrayBase, entries);
        return entries;
      }

      if (!value || typeof value !== 'object') {
        if (!base) {
          return entries;
        }

        const description =
          typeof value === 'string'
            ? value.trim()
            : value === null || value === undefined
              ? ''
              : String(value);

        // Variables that describe object/array containers are not selectable mappers.
        const isContainerType = /\(\s*(array|object)\s*\)/i.test(description);
        if (isContainerType) {
          return entries;
        }

        entries.push({
          path: base,
          description,
          type: this.extractVariableType(description),
        });
        return entries;
      }

      Object.keys(value).forEach((key) => {
        const keySegment = String(key || '').trim();
        if (!keySegment) {
          return;
        }
        const path = base ? `${base}.${keySegment}` : keySegment;
        this.getNestedVariableEntries(value[key], path, entries);
      });

      return Array.from(new Map(entries.map((entry) => [entry.path, entry])).values());
    },

    extractVariableType(description = '') {
      if (!description || typeof description !== 'string') {
        return 'value';
      }

      const match = description.match(/^\(\s*([^)]+)\s*\)/);
      if (!match) {
        return 'value';
      }

      return match[1].trim().toLowerCase();
    },

    normalizeVariablePath(path = '') {
      if (!path || typeof path !== 'string') {
        return '';
      }

      return path
        .split('.')
        .map((segment) => segment.trim())
        .filter((segment) => segment !== '')
        .join('.')
        .replace(/\s+/g, '');
    },

    formatVariableMeta(type = '') {
      if (!type || typeof type !== 'string') {
        return 'value';
      }

      return type.trim().toLowerCase();
    },

    getEditorRef(eventId) {
      const ref = this.$refs[`responseEditor-${eventId}`];
      if (Array.isArray(ref)) {
        return ref[0] || null;
      }
      return ref || null;
    },

    getJsonEditorInstance(eventId) {
      const editorRef = this.getEditorRef(eventId);
      return editorRef?.editor || editorRef?.$refs?.jsonEditor?.editor;
    },

    getAceEditor(jsonEditor) {
      if (!jsonEditor) {
        return null;
      }

      if (jsonEditor.aceEditor) {
        return jsonEditor.aceEditor;
      }

      const ace = window.ace;
      if (!ace || !jsonEditor.container) {
        return null;
      }

      const aceDom = jsonEditor.container.querySelector('.ace_editor');
      if (!aceDom) {
        return null;
      }

      return ace.edit(aceDom);
    },

    // For a variable like "transaction_items.0.product_id", scan the editor content
    // and increment the index until we find one not yet present as a full {$...} token.
    resolveArrayIndex(variable, fullContent) {
      // Only touch variables that contain a numeric index segment
      if (!/\.\d+[.\b]/.test(variable) && !/\.\d+$/.test(variable)) {
        return variable;
      }
      for (let idx = 0; idx <= 99; idx++) {
        const candidate = variable.replace(/\.(\d+)\./g, `.${idx}.`).replace(/\.(\d+)$/, `.${idx}`);
        if (!fullContent.includes(`{$${candidate}}`)) {
          return candidate;
        }
      }
      return variable;
    },

    buildVariableCompleter(eventId) {
      return {
        identifierRegexps: [/[A-Za-z0-9_.$]/],

        getCompletions: (editor, session, pos, prefix, callback) => {
          const currentLine = session.getLine(pos.row) || '';
          const beforeCursor = currentLine.slice(0, pos.column);
          const triggerIndex = beforeCursor.lastIndexOf('{$');

          if (triggerIndex < 0) {
            callback(null, []);
            return;
          }

          const typedValue = beforeCursor.slice(triggerIndex + 2);
          if (/\s|\}/.test(typedValue)) {
            callback(null, []);
            return;
          }

          const eventVariables = this.responseVariablesByEvent[eventId] || [];
          const fullContent = session.getValue();
          const matched = eventVariables
            .filter((variable) => {
              const path = this.normalizeVariablePath(variable?.path || '');
              return path.toLowerCase().includes(typedValue.toLowerCase());
            })
            .map((variable) => {
              const normalizedPath = this.normalizeVariablePath(variable.path);
              const resolved = this.resolveArrayIndex(normalizedPath, fullContent);
              const displayPath = this.normalizeVariablePath(resolved);
              const resolvedDescription = variable?.description || '';
              const variableType = variable?.type || this.extractVariableType(resolvedDescription);
              return {
                caption: `{$${displayPath}}`,
                value: `$${displayPath}}`,
                path: displayPath,
                meta: this.formatVariableMeta(variableType),
                docText: resolvedDescription || `{$${displayPath}}`,
                score: 1000,
              };
            });

          callback(null, matched);
        },
      };
    },

    setupAutocomplete(item, attempt = 0) {
      if (!item?.event_id || this.autocompleteBoundEditors[item.event_id]) {
        return;
      }

      const jsonEditor = this.getJsonEditorInstance(item.event_id);
      const aceEditor = this.getAceEditor(jsonEditor);

      // The editor is mounted lazily inside expansion content, so retry a few times.
      if (!aceEditor) {
        if (attempt < 10) {
          setTimeout(() => {
            this.setupAutocomplete(item, attempt + 1);
          }, 120);
        }
        return;
      }

      aceEditor.setOptions({
        enableBasicAutocompletion: true,
        enableLiveAutocompletion: false,
        enableSnippets: false,
      });

      const variableCompleter = this.buildVariableCompleter(item.event_id);
      aceEditor.completers = [variableCompleter];

      aceEditor.on('change', (delta) => {
        if (delta?.action !== 'insert') {
          return;
        }
        const inserted = (delta.lines || []).join('\n');
        const cursorPos = aceEditor.getCursorPosition();
        const currentLine = aceEditor.session.getLine(cursorPos.row) || '';
        const beforeCursor = currentLine.slice(0, cursorPos.column);

        // Trigger immediately when '$' is typed right after '{'.
        const typedDollarAfterBrace = inserted === '$' && beforeCursor.endsWith('{');
        if (inserted.includes('{$') || typedDollarAfterBrace || beforeCursor.endsWith('{$')) {
          setTimeout(() => {
            aceEditor.execCommand('startAutocomplete');
            if (aceEditor.completer?.activated) {
              aceEditor.completer.updateCompletions(true);
            }
          }, 0);
        }
      });

      this.autocompleteBoundEditors = {
        ...this.autocompleteBoundEditors,
        [item.event_id]: true,
      };
    },

    async saveMapper(item) {
      this.validateMapper(item);
      if (!this.valid.mapper[item.event_name]) {
        return false;
      }
      let res;
      if (item.store_event_response_mapper_id) {
        res = await this.updateEventMapper({
          id: item.store_event_response_mapper_id,
          body: { response: item.mapper },
        });
      } else {
        res = await this.createEventMapper({
          event_id: item.event_id,
          store_id: localStorage.getItem('storeId'),
          response: item.mapper,
        });
      }
      this.setNotification({
        show: true,
        type: !res || res.error ? 'error' : 'success',
        text: res?.message?.replaceAll('_', ' ') || 'ERROR',
      });
    },

    getPermission(path) {
      return (
        this.superUser ||
        (eval('this.permissions.developers.webhooks_responses.' + path) &&
          eval('this.storePermissions.developers.webhooks_responses.' + path))
      );
    },

    showJsonError(item) {
      // this.validateMapper(item)
      this.error.mapper[item.event_name] = this.errorMessages.mapperInvalid[this.lang];
      this.valid.mapper[item.event_name] = false;
    },
  },

  watch: {
    permission: {
      handler(val) {
        if (!val && !this.superUser) {
          this.$router.replace(`/${this.lang}/loyaltyPanel/forbidden-gateway`);
        }
      },
    },
  },

  mounted() {
    this.getAllResponses();
    this.getEventMappers();
  },
};
</script>

<style>
.mapping-layout-toggle {
  transition: all 0.22s ease;
}

.response-structure-col {
  transition: all 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  transform-origin: left center;
  overflow: hidden;
}

.response-panels-row {
  flex-wrap: nowrap;
}

.response-structure-toggle-wrap {
  margin-bottom: -6px;
  z-index: 2;
}

.response-structure-toggle-btn {
  min-width: 44px !important;
  width: 44px;
  height: 30px !important;
  border-radius: 8px !important;
  border-color: rgba(0, 195, 255, 0.5) !important;
  background: rgba(0, 195, 255, 0.18) !important;
  box-shadow: inset 0 -1px 0 rgba(0, 195, 255, 0.3);
  margin-bottom: -19px;
  margin-top: 8px;
  border-bottom: none;
}

.response-structure-toggle-btn--open {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.response-structure-toggle-btn:hover {
  background: rgba(0, 195, 255, 0.26) !important;
  transform: translateY(-1px);
}

.response-structure-frame {
  background: rgba(0, 195, 255, 0.1);
  border: 1px solid rgba(0, 195, 255, 0.35);
  max-height: 0px;
}

.response-structure-frame--open {
  border-top-left-radius: 0 !important;
  max-height: none;
}

.response-mapping-col {
  transition: all 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.response-structure-divider {
  transition: all 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}

.event-map-input {
  align-items: stretch;
  display: flex;
  height: 100%;
}

.event-map-input .v-input__slot {
  align-items: stretch;
  display: flex;
  height: 100%;
}
.event-map-input .v-input__control {
  padding-bottom: 20px;
}
.event-map-input .v-text-field__details {
  position: absolute;
  bottom: 0;
}
.green-area {
  background: rgba(5, 236, 128, 0.1);
}
.red-area {
  background: rgba(255, 0, 0, 0.1);
}

.ace_editor.ace_autocomplete {
  width: 680px !important;
  max-width: min(90vw, 680px);
}

.ace_autocomplete .ace_completion-caption {
  max-width: 430px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ace_autocomplete .ace_completion-meta {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #607d8b;
}

.ace_doc-tooltip {
  max-width: 620px;
  white-space: pre-wrap;
}
</style>
